import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import MuiBreadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import {
    generatePath,
    Link as RouterLink,
    matchPath,
    useLocation,
    type Params,
} from 'react-router';

import { useGetSubscriptionByIdQuery } from 'src/apiClients';
import {
    APP_ROUTES,
    type BreadcrumbCrumb,
    type RouteDefinition,
} from 'src/hooks/use-imago-mp-routes';
import { i18n as routesI18n } from 'src/hooks/use-imago-mp-routes.i18n';

type ResolvedMatch = {
    crumbs: BreadcrumbCrumb[];
    params: Params;
    fallbackTo: string | null;
};

const flattenRoutes = (routes: readonly RouteDefinition[]): RouteDefinition[] =>
    routes.flatMap((route) =>
        route.children?.length ? [route, ...flattenRoutes(route.children)] : [route],
    );

const findRouteMatch = (pathname: string): ResolvedMatch | null => {
    let isFallback = false;
    let candidate = pathname;
    while (candidate) {
        for (const route of flattenRoutes(APP_ROUTES)) {
            if (!route.breadcrumbs.length) continue;
            const match = matchPath({ path: route.path, end: true }, candidate);
            if (match) {
                return {
                    crumbs: route.breadcrumbs,
                    params: match.params,
                    fallbackTo: isFallback ? candidate : null,
                };
            }
        }
        const shorter = candidate.replace(/\/[^/]*$/, '');
        if (shorter === candidate) break;
        candidate = shorter;
        isFallback = true;
    }
    return null;
};

export const Breadcrumbs = () => {
    const intl = useIntl();
    const { pathname } = useLocation();

    const resolved = useMemo(() => findRouteMatch(pathname), [pathname]);

    const instanceId = resolved?.params.id;
    const { data: instance } = useGetSubscriptionByIdQuery(instanceId ?? '', {
        skip: !instanceId,
    });

    if (!resolved?.crumbs.length) {
        return null;
    }

    const { crumbs, params, fallbackTo } = resolved;

    const labelFor = (crumb: BreadcrumbCrumb): string => {
        if (crumb.label.id === routesI18n.instanceDetail.id && instance) {
            return instance.subscriptionName || intl.formatMessage(routesI18n.untitledInstance);
        }
        return intl.formatMessage(crumb.label);
    };

    return (
        <MuiBreadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
            {crumbs.map((crumb, index) => {
                const isLast = index === crumbs.length - 1;
                const key = `${crumb.label.id}-${index}`;
                const to = isLast && fallbackTo ? fallbackTo : crumb.to;

                if (!to) {
                    return (
                        <Typography key={key} color="text.primary" variant="body2">
                            {labelFor(crumb)}
                        </Typography>
                    );
                }

                return (
                    <Link
                        key={key}
                        component={RouterLink}
                        to={isLast ? to : generatePath(to, params)}
                        underline="hover"
                        color="inherit"
                        variant="body2"
                    >
                        {labelFor(crumb)}
                    </Link>
                );
            })}
        </MuiBreadcrumbs>
    );
};
